import { Combobox } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CUSTOM_CHART_BAR_ORDERS,
  CUSTOM_CHART_BAR_ORIENTATIONS,
  CUSTOM_CHART_PIVOT_VALUE_CODE,
  type ChartCategoryColors,
  type ChartValueColorRule,
  type CustomChartBarOrder,
  type CustomChartBarOrientation,
  type CustomChartColor,
  type CustomChartTableColumn,
  type CustomChartType,
} from '@/entities/dashboards';
import { TruncatedText } from '@/shared/ui';
import { shownChartLabel } from '../DashboardPresetGrid/chartValues';
import { GROUP_COLUMN_CODE } from '../DashboardPresetGrid/views/chartTable';
import { ChartColorPicker } from './ChartColorPicker';
import { ChartValueColorRulesField } from './ChartValueColorRulesField';

type Props = {
  chartType: CustomChartType;
  barOrientation: CustomChartBarOrientation;
  onChangeBarOrientation: (orientation: CustomChartBarOrientation) => void;
  /** Порядок элементов столбчатой диаграммы; `null` — как дала выборка */
  barOrder: CustomChartBarOrder | null;
  onChangeBarOrder: (order: CustomChartBarOrder | null) => void;
  /** Категории последней полученной выборки — им и задаются цвета */
  categories: string[];
  categoryColors: ChartCategoryColors;
  onChangeCategoryColors: (colors: ChartCategoryColors) => void;
  valueColorRules: ChartValueColorRule[];
  onChangeValueColorRules: (rules: ChartValueColorRule[]) => void;
  /** Колонки последнего наполнения таблицы — из них выбираются окрашиваемые */
  colorableColumns: CustomChartTableColumn[];
  coloredColumns: string[];
  onChangeColoredColumns: (columns: string[]) => void;
};

/** Типы, у которых цвет означает категорию, а не величину: им и задаются цвета категорий. */
const CATEGORY_COLOR_CHART_TYPES: CustomChartType[] = ['bar', 'pie', 'selection-breakdown'];

/**
 * Таблицы: их раскраска ограничена выбранными колонками, а поимённо красятся текстовые значения
 * этих колонок.
 */
const TABLE_CHART_TYPES: CustomChartType[] = ['table', 'grouped-table'];

/**
 * Типы, у которых величина нарисована или показана числом: их и красят правила величины.
 *
 * Круговой диаграммы в перечне нет — там цвет означает категорию; нет и типов, показывающих
 * результат текстом: красить в них нечего.
 */
const VALUE_RULE_CHART_TYPES: CustomChartType[] = [
  'bar',
  'line',
  'heatmap',
  'table',
  'grouped-table',
  'selection-breakdown',
];

/** Подписи направлений элементов — ключи перевода по коду направления. */
const BAR_ORIENTATION_LABEL_KEY: Record<
  CustomChartBarOrientation,
  'presets.editor.barOrientations.horizontal' | 'presets.editor.barOrientations.vertical'
> = {
  horizontal: 'presets.editor.barOrientations.horizontal',
  vertical: 'presets.editor.barOrientations.vertical',
};

/** Код порядка элементов в выборе: порядок выборки и каждый порядок каталога. */
type BarOrderCode = 'payload' | `${CustomChartBarOrder['by']}-${CustomChartBarOrder['direction']}`;

const barOrderCode = (order: CustomChartBarOrder | null): BarOrderCode =>
  order ? `${order.by}-${order.direction}` : 'payload';

/** Подписи порядков элементов — ключи перевода по коду порядка. */
const BAR_ORDER_LABEL_KEY: Record<BarOrderCode, `presets.editor.barOrders.${BarOrderCode}`> = {
  payload: 'presets.editor.barOrders.payload',
  'value-desc': 'presets.editor.barOrders.value-desc',
  'value-asc': 'presets.editor.barOrders.value-asc',
  'label-asc': 'presets.editor.barOrders.label-asc',
  'label-desc': 'presets.editor.barOrders.label-desc',
};

/**
 * Оформление диаграммы в её свойствах: направление и порядок элементов, цвета категорий, окрашиваемые
 * колонки таблицы и правила раскраски по величине.
 *
 * Поля показаны там, где им есть что задавать: направление и порядок — только у столбчатой диаграммы, цвета
 * категорий — там, где цвет означает категорию, окрашиваемые колонки — у таблиц, а цвета значений
 * у них — когда колонки выбраны: без выбора цвета значений не действуют. Правила величины — везде,
 * кроме круговой.
 *
 * Заданное применяется к диаграмме сразу, ещё до сохранения свойств: цвет, направление и порядок выбирают,
 * глядя на саму диаграмму, а не вслепую.
 */
export const ChartAppearanceFields: FC<Props> = ({
  chartType,
  barOrientation,
  onChangeBarOrientation,
  barOrder,
  onChangeBarOrder,
  categories,
  categoryColors,
  onChangeCategoryColors,
  valueColorRules,
  onChangeValueColorRules,
  colorableColumns,
  coloredColumns,
  onChangeColoredColumns,
}) => {
  const { t } = useTranslation('qualityDashboard');

  const orientationOptions = useMemo(
    () =>
      CUSTOM_CHART_BAR_ORIENTATIONS.map((orientation) => ({
        label: t(BAR_ORIENTATION_LABEL_KEY[orientation]),
        value: orientation,
      })),
    [t],
  );

  const orderOptions = useMemo(
    () =>
      [null, ...CUSTOM_CHART_BAR_ORDERS].map((order) => ({
        label: t(BAR_ORDER_LABEL_KEY[barOrderCode(order)]),
        value: barOrderCode(order),
      })),
    [t],
  );

  // Категория, которой цвет задан, остаётся в перечне и после того, как выпала из выборки: цвет
  // за ней сохранён, и снять его иначе было бы негде.
  const coloredCategories = useMemo(() => {
    const named = Object.keys(categoryColors).filter((label) => !categories.includes(label));
    return [...categories, ...named];
  }, [categories, categoryColors]);

  // Выбранная колонка остаётся в перечне и тогда, когда в наполнении её нет: снять выбор иначе
  // было бы негде. Развёрнутые колонки названы одной, колонка группы — тем же, что в таблице.
  const columnOptions = useMemo(() => {
    const columnLabel = (column: CustomChartTableColumn) => {
      if (column.code === CUSTOM_CHART_PIVOT_VALUE_CODE) return t('presets.editor.pivotColumns');
      // Колонка группы названия из наполнения не несёт: в таблице её называет само место показа
      if (column.code === GROUP_COLUMN_CODE && !column.title) return t('presets.chart.groupColumn');
      return column.title;
    };
    const known = colorableColumns.map((column) => ({ label: columnLabel(column), value: column.code }));
    const absent = coloredColumns
      .filter((code) => !colorableColumns.some((column) => column.code === code))
      .map((code) => ({ label: code, value: code }));
    return [...known, ...absent];
  }, [colorableColumns, coloredColumns, t]);

  const isOrientationShown = chartType === 'bar';
  const isTable = TABLE_CHART_TYPES.includes(chartType);
  const isCategoryColorsShown =
    CATEGORY_COLOR_CHART_TYPES.includes(chartType) || (isTable && coloredColumns.length > 0);
  const isValueRulesShown = VALUE_RULE_CHART_TYPES.includes(chartType);

  if (!isOrientationShown && !isTable && !isCategoryColorsShown && !isValueRulesShown) return null;

  const selectOrientation = (value: unknown) =>
    onChangeBarOrientation((value as CustomChartBarOrientation | undefined) ?? barOrientation);

  // Порядок выборки кодом каталога не назван — он и остаётся, когда выбранный код в каталоге не найден
  const selectOrder = (value: unknown) =>
    onChangeBarOrder(CUSTOM_CHART_BAR_ORDERS.find((order) => barOrderCode(order) === value) ?? null);

  const selectColoredColumns = (values: unknown[]) => onChangeColoredColumns(values.map(String));

  const setCategoryColor = (label: string, color: CustomChartColor | undefined) => {
    const next = { ...categoryColors };
    // Снятый цвет убирается из состава, а не остаётся пустым значением: категория без записи
    // красится набором по своему порядку.
    if (color) next[label] = color;
    else delete next[label];
    onChangeCategoryColors(next);
  };

  return (
    <div className="flex flex-col gap-4 border-t pt-4">
      <p className="text-sm font-medium">{t('presets.editor.appearance')}</p>

      {isOrientationShown && (
        <Combobox
          label={t('presets.editor.barOrientation')}
          value={barOrientation}
          onChange={selectOrientation}
          options={orientationOptions}
          isSearchable={false}
          className="w-full @md/chart-properties:w-1/2"
        />
      )}

      {isOrientationShown && (
        <Combobox
          label={t('presets.editor.barOrder')}
          value={barOrderCode(barOrder)}
          onChange={selectOrder}
          options={orderOptions}
          isSearchable={false}
          className="w-full @md/chart-properties:w-1/2"
        />
      )}

      {isTable &&
        (columnOptions.length === 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{t('presets.editor.coloredColumns')}</p>
            <p className="text-xs text-muted-foreground">{t('presets.editor.coloredColumnsEmpty')}</p>
          </div>
        ) : (
          <Combobox
            isMulti
            label={t('presets.editor.coloredColumns')}
            value={coloredColumns}
            onChange={selectColoredColumns}
            options={columnOptions}
            placeholder={t('presets.editor.coloredColumnsPlaceholder')}
            isSearchable={false}
            className="w-full @md/chart-properties:w-1/2"
          />
        ))}

      {isCategoryColorsShown && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">
            {t(isTable ? 'presets.editor.valueColors' : 'presets.editor.categoryColors')}
          </p>
          {coloredCategories.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {t(isTable ? 'presets.editor.valueColorsEmpty' : 'presets.editor.categoryColorsEmpty')}
            </p>
          ) : (
            coloredCategories.map((label) => (
              <div key={label} className="flex flex-wrap items-center gap-3">
                {/* Место у названия ограничено: не поместившееся раскрывается подсказкой целиком.
                    Категория названа так же, как подписана на диаграмме, а значение таблицы — как
                    в её ячейке. */}
                <TruncatedText className="max-w-60 text-sm">{isTable ? label : shownChartLabel(label)}</TruncatedText>
                <ChartColorPicker
                  value={categoryColors[label]}
                  onChange={(color) => setCategoryColor(label, color)}
                  isResettable
                />
              </div>
            ))
          )}
        </div>
      )}

      {isValueRulesShown && <ChartValueColorRulesField rules={valueColorRules} onChange={onChangeValueColorRules} />}
    </div>
  );
};
